<template>
  <q-btn-dropdown
    id="agents-table-quick-filter-dropdown"
    data-testid="agents-table-quick-filter-dropdown"
    :menu-offset="[0, 5]"
    dropdown-icon="icon-down-small"
    no-icon-animation
    content-class="agents-table-quick-filter-dropdown-content gordita-400-12"
    class="incredi-dropdown agents-table-quick-filter-dropdown gordita-400-12 !text-[12px] text-brand_almost_black"
    dense
    no-caps
    :label="curQuickFilterLabel"
    outline
    :ripple="false"
  >
    <q-list>
      <q-item
        v-close-popup
        dense
        clickable
        class="agents-table-quick-filter-item"
        :class="{ 'agents-table-quick-filter-item-selected ibf-gordita-12-m': quickFilterState.showAll }"
        data-testid="agents-table-quick-filter-showAll"
        @click="updateQuickFilter('showAll')"
      >
        <q-item-section>
          <q-item-label>{{ filterLabelMap[osType].showAll }}</q-item-label>
        </q-item-section>
      </q-item>

      <q-item
        v-close-popup
        dense
        clickable
        class="agents-table-quick-filter-item"
        :class="{ 'agents-table-quick-filter-item-selected ibf-gordita-12-m': quickFilterState.active }"
        data-testid="agents-table-quick-filter-online"
        @click="updateQuickFilter('active')"
      >
        <q-item-section>
          <q-item-label>{{ filterLabelMap[osType].active }}</q-item-label>
        </q-item-section>
      </q-item>

      <q-item
        v-if="osType === OsType.Windows"
        v-close-popup
        dense
        clickable
        class="agents-table-quick-filter-item"
        :class="{ 'agents-table-quick-filter-item-selected ibf-gordita-12-m': quickFilterState.inProcess }"
        data-testid="agents-table-quick-filter-inProcess"
        @click="updateQuickFilter('inProcess')"
      >
        <q-item-section>
          <q-item-label>{{ filterLabelMap[osType].inProcess }}</q-item-label>
        </q-item-section>
      </q-item>
      <q-item
        v-close-popup
        dense
        clickable
        class="agents-table-quick-filter-item"
        :class="{ 'agents-table-quick-filter-item-selected ibf-gordita-12-m': quickFilterState.agentsIdFilter }"
        data-testid="agents-table-quick-filter-selectedAgents"
        @click="handleSelectedAgents('agentsIdFilter')"
      >
        <q-item-section>
          <q-item-label>{{ filterLabelMap[osType].agentsIdFilter }}</q-item-label>
        </q-item-section>
      </q-item>
      <q-item
        v-close-popup
        dense
        clickable
        class="agents-table-quick-filter-item"
        :class="{ 'agents-table-quick-filter-item-selected ibf-gordita-12-m': quickFilterState.incompatibleVersion }"
        data-testid="agents-table-quick-filter-incompatibleVersion"
        @click="updateQuickFilter('incompatibleVersion')"
      >
        <q-item-section>
          <q-item-label>{{ filterLabelMap[osType].incompatibleVersion }}</q-item-label>
        </q-item-section>
      </q-item>
      <q-item
        v-close-popup
        dense
        clickable
        class="agents-table-quick-filter-item"
        :class="{ 'agents-table-quick-filter-item-selected ibf-gordita-12-m': quickFilterState.unlicensedAgents }"
        data-testid="agents-table-quick-filter-unlicensedAgents"
        @click="updateQuickFilter('unlicensedAgents')"
      >
        <q-item-section>
          <q-item-label>{{ filterLabelMap[osType].unlicensedAgents }}</q-item-label>
        </q-item-section>
      </q-item>
    </q-list>
  </q-btn-dropdown>
</template>

<script>
import { defineComponent, inject } from 'vue';
import { IKAgentsDrawerFilter } from '@/types/injectionKeys';
import { IKAgentsSelected } from '@/views/agents/components/agents-table/agents-table.InjectionKeys';
import { useMatomo } from '@/services/matomo/matomo.service';
import { IKPlatformAuthentication } from '@/modules/platformAuthentication/platformAuthentication.injectionKeys';
import { OsType } from '@/modules/platformAuthentication/platformAuthentication.const';

export default defineComponent({
  props: {
    quickFilterState: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const agentsFilter = inject(IKAgentsDrawerFilter);
    const selectedAgents = inject(IKAgentsSelected);
    const matomo = useMatomo();
    const platformAuthentication = inject(IKPlatformAuthentication);
    return { agentsFilter, selectedAgents, matomo, platformAuthentication, OsType };
  },
  data() {
    return {
      filterLabelMap: {
        windows: {
          showAll: 'Show All',
          active: 'Online',
          inProcess: 'In Process',
          agentsIdFilter: 'Selected Agents',
          incompatibleVersion: 'Incompatible Version',
          unlicensedAgents: 'Missing Roles',
        },
        linux: {
          showAll: 'Show All',
          active: 'Online',
          agentsIdFilter: 'Selected Agents',
          incompatibleVersion: 'Incompatible Version',
          unlicensedAgents: 'Unsubscribed Agents',
        },
        mac: {
          showAll: 'Show All',
          active: 'Online',
          agentsIdFilter: 'Selected Agents',
          incompatibleVersion: 'Incompatible Version',
          unlicensedAgents: 'Unsubscribed Agents',
        },
      },
    };
  },
  computed: {
    osType() {
      return this.platformAuthentication?.osType.value;
    },
    curQuickFilterLabel() {
      const curFilter = Object.keys(this.quickFilterState).find(key => this.quickFilterState[key]);
      return this.filterLabelMap[this.osType][curFilter];
    },
  },
  methods: {
    handleSelectedAgents(newQuickFilter) {
      let quickFilter = [];
      Object.keys(this.quickFilterState).forEach(key => quickFilter.push({ group: 'quickFilter', filter: key, value: false }));
      quickFilter = quickFilter.map(filter => {
        if (filter.filter === newQuickFilter) filter.value = true;
        return filter;
      });
      this.agentsFilter?.updateFilter(quickFilter);
    },
    updateQuickFilter(newQuickFilter) {
      let quickFilter = [];
      Object.keys(this.quickFilterState).forEach(key => quickFilter.push({ group: 'quickFilter', filter: key, value: false }));
      quickFilter = quickFilter.map(filter => {
        if (filter.filter === newQuickFilter) filter.value = true;
        return filter;
      });
      this.agentsFilter?.updateFilter(quickFilter);
      this.agentsFilter?.setGQL_AgentsQuickFilter(newQuickFilter);
      this.selectedAgents?.clearSelection();
      this.matomo?.trackEvent({
        category: 'Quick Filter',
        action: this.curQuickFilterLabel,
      });
    },
  },
});
</script>

<style lang="scss">
.agents-table-quick-filter-item {
  color: $-text--dark !important;
  * {
    background: none !important;
  }
}
.agents-table-quick-filter-item-selected {
  color: $-text-text-primary !important;
}

#agents-table-quick-filter-dropdown {
  .q-focus-helper {
    &:hover {
      background: $-hovers-hover-grey !important;
    }
  }
  padding: 0 8px;
  &::before {
    border: none !important;
  }

  &[aria-expanded='true'] {
    .q-focus-helper {
      opacity: 0;
    }
    background: $-hovers-hover-primary !important ;
  }
  /* margin: 0 17px; */
  border-radius: 2px;
  /* height: 36px; */
  .q-icon {
    margin-left: 8px;
    transform: rotate(0deg) !important;
    font-size: 10px !important;
    /* display: none; */
  }

  .q-btn__content {
    position: relative;
    top: 1px;
    /* color: $app_almost_black; */
  }
  .q-btn__wrapper {
    line-height: 0;
    padding: 10.5px 8px;
  }
}

.agents-table-quick-filter-dropdown-content {
  --q-transition-duration: 0 !important;
  transition: none !important;
  .q-list {
    width: 145px !important;
  }
  padding: 8px;
  border-radius: 2px !important;
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.2) !important;
  .q-item__section {
    /* height: 28px; */
  }
  .q-item {
    width: 146px;
    padding: 6px 7px;
    /* width: 138px; */
    border-radius: 2px;
    &:hover {
      background-color: $-hovers-hover-primary;
    }

    /* &:focus {
      background-color: $-system-app-primary-bg;
    } */

    /* .q-focus-helper {
      &:hover {
        background-color: $-system-app-primary-bg;
      }
      &:focus {
        background-color: $-system-app-primary-bg;
      }
    } */
  }
}
</style>
